import { useRef, useState } from 'react';
import { Share2, Download, X, Check } from 'lucide-react';
import html2canvas from 'html2canvas';

const WIN_MESSAGES = [
  "Small wins compound. This is proof.",
  "You said you'd do it. And you did.",
  "Not perfect. Done. That's what matters.",
  "Your brain tried to stall. You moved anyway.",
  "Progress over perfection, every single time.",
  "One step at a time — look how far you've come.",
];

function ShareWinCard({ task, childTasks, theme, onClose }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const completedChildren = childTasks.filter(c => c.completed);
  const totalChildren = childTasks.length;
  const progress = totalChildren > 0 ? Math.round((completedChildren.length / totalChildren) * 100) : (task.completed ? 100 : 0);
  const message = WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)];

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `win-${task.title.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Failed to generate image:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
      });
      canvas.toBlob(async (blob) => {
        if (navigator.share && blob) {
          const file = new File([blob], 'my-win.png', { type: 'image/png' });
          try {
            await navigator.share({
              title: `I completed: ${task.title}`,
              text: message,
              files: [file],
            });
          } catch {
            // User cancelled or share failed, fall back to download
            handleDownload();
          }
        } else {
          handleDownload();
        }
        setDownloading(false);
      });
    } catch {
      setDownloading(false);
      handleDownload();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-win-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-w-md w-full">
        {/* Action buttons */}
        <div className="flex justify-end gap-2 mb-3 relative z-10">
          <button
            onClick={handleShare}
            disabled={downloading}
            data-testid="share-win-share-btn"
            className="bg-[#E07A5F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#D46B4E] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            data-testid="share-win-download-btn"
            className="bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors inline-flex items-center gap-2 backdrop-blur disabled:opacity-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Download className="w-4 h-4" />
            Save
          </button>
          <button
            onClick={onClose}
            data-testid="share-win-close-btn"
            className="bg-white/20 text-white p-2 rounded-lg hover:bg-white/30 transition-colors backdrop-blur"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* The card that gets captured as image */}
        <div
          ref={cardRef}
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            padding: '2rem',
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '2rem', height: '2rem', borderRadius: '50%',
                background: '#E07A5F', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check style={{ width: '1rem', height: '1rem', color: 'white' }} />
              </div>
              <span style={{
                fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.75rem',
                textTransform: 'uppercase', letterSpacing: '0.15em', color: '#E07A5F', fontWeight: 600,
              }}>
                Win Unlocked
              </span>
            </div>
            <h2 style={{
              fontFamily: 'Manrope, sans-serif', fontSize: '1.5rem', fontWeight: 700,
              color: '#ffffff', lineHeight: 1.3, marginBottom: '0.5rem',
            }}>
              {task.title}
            </h2>
            {/* Progress bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                flex: 1, height: '6px', borderRadius: '3px',
                background: 'rgba(255,255,255,0.1)',
              }}>
                <div style={{
                  height: '100%', borderRadius: '3px',
                  background: progress === 100 ? '#81B29A' : '#E07A5F',
                  width: `${progress}%`, transition: 'width 0.3s',
                }} />
              </div>
              <span style={{
                fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.875rem',
                color: progress === 100 ? '#81B29A' : '#E07A5F', fontWeight: 600,
              }}>
                {progress}%
              </span>
            </div>
          </div>

          {/* Completed subtasks */}
          {completedChildren.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              {completedChildren.slice(0, 8).map((child, i) => (
                <div key={child.task_id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.375rem 0',
                  borderBottom: i < Math.min(completedChildren.length, 8) - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}>
                  <div style={{
                    width: '1.25rem', height: '1.25rem', borderRadius: '50%',
                    background: '#81B29A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Check style={{ width: '0.7rem', height: '0.7rem', color: 'white' }} />
                  </div>
                  <span style={{
                    fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.8125rem',
                    color: 'rgba(255,255,255,0.7)', textDecoration: 'line-through',
                  }}>
                    {child.title}
                  </span>
                </div>
              ))}
              {completedChildren.length > 8 && (
                <span style={{
                  fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.4)', display: 'block', marginTop: '0.25rem',
                }}>
                  +{completedChildren.length - 8} more completed
                </span>
              )}
            </div>
          )}

          {/* Remaining (if partial) */}
          {totalChildren > completedChildren.length && (
            <div style={{ marginBottom: '1.5rem' }}>
              <span style={{
                fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                {totalChildren - completedChildren.length} remaining — still going
              </span>
            </div>
          )}

          {/* Quote */}
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            paddingTop: '1rem',
          }}>
            <p style={{
              fontFamily: 'Manrope, sans-serif', fontSize: '0.9375rem',
              color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', lineHeight: 1.5,
            }}>
              "{message}"
            </p>
          </div>

          {/* Branding */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '1.25rem', paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}>
            <span style={{
              fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.6875rem',
              color: 'rgba(255,255,255,0.25)', letterSpacing: '0.05em',
            }}>
              Procrastination Popper
            </span>
            <span style={{
              fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '0.6875rem',
              color: 'rgba(255,255,255,0.25)',
            }}>
              {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareWinCard;
